// The in-memory twin of an agent's knowledge base (DOCS-KB; `cell.agent_knowledge_source`, the
// routes in contracts/api/knowledge.ts and `pages.readers`), with the API's rules:
// - an agent is a digital employee of the company or a person's crew ("{name}'s agent"); any
//   other membership is 404. A crew's list is its owner's and the managers' only;
// - the list is base plan (`core`); adding and removing are the `agents` add-on's (off: 403
//   `feature_disabled`, grace: 403 `feature_read_only`) and need `can_edit`: a company manager,
//   the holder of the DE's supervisor seat, or the crew's owner (403 `forbidden` otherwise);
// - a source names one page, one space or one page kind; adding a target already there is a
//   no-op; an unknown page or space is 422 (the API's `invalid_reference`);
// - `page_count` is the live pages the source resolves to now; `last_read_at` is stamped by runs
//   (seeded here: the demo DE read two of its sources, one never);
// - `pages.readers`: the agents whose sources cover a page, directly, by its space or its kind.
// `fake-knowledge-contract.test.ts` holds it to the contracts.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, type Principal, problem, validation } from "./fake";
import { crewMembershipIdOf, type FakeDe, uid } from "./fake-data";

type Knowledge = OperationOutput<"agentKnowledge.list">;
type SourceView = Knowledge["data"][number];
type Source = Omit<SourceView, "page" | "space" | "page_count">;
type PageSummary = OperationOutput<"pages.list">["data"][number];
type PageKind = NonNullable<Source["page_kind"]>;
type Agent = Knowledge["agent"] & { supervisor: string | null; owner: string | null };

export type KnowledgeDeps = {
  now: () => Date;
  desOf: (companyId: string) => FakeDe[];
  /** The company's people (crew owners), with their names. */
  peopleOf: (companyId: string) => { membership_id: string; name: string }[];
  /** Every page of the company, archived ones included (the pages twin, read as a manager). */
  pagesOf: (p: Principal) => PageSummary[];
  /** The company's spaces (the Work twin's). */
  spacesOf: (companyId: string) => { id: string; name: string; kind: string }[];
  /** The `agents` add-on (with `memory`, which it bundles): on, grace or off. */
  agentsState: (p: Principal) => "on" | "grace" | "off";
  /** Client viewers read no pages (the pages twin's rule). */
  readsPages: (p: Principal) => boolean;
};

const WRITABLE_KINDS: readonly PageKind[] = [
  "page",
  "sop",
  "policy",
  "kb",
  "vision",
  "journal",
  "decision",
  "strategy",
] as PageKind[];
const ORDER: Record<Source["source_kind"], number> = { page: 0, space: 1, kind: 2 };
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const forbidden = (detail: string) => problem(403, "forbidden", "Forbidden", detail);

function featureDisabledAgents(): Response {
  return new Response(
    JSON.stringify({
      type: "https://companyos.app/errors/feature_disabled",
      title: "Feature not enabled",
      status: 403,
      code: "feature_disabled",
      feature: "agents",
      request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
      detail: "The agents add-on is not enabled for this company.",
    }),
    { status: 403, headers: { "content-type": "application/problem+json" } },
  );
}

export function createKnowledgeFake(deps: KnowledgeDeps): {
  handlers: Partial<Record<RouteId, Handler>>;
} {
  const stores = new Map<string, Source[]>();
  let seq = 0x100;
  const nextId = () => uid(0x7b00, ++seq);
  const stamp = () => deps.now().toISOString();
  const hoursAgo = (h: number) => new Date(deps.now().getTime() - h * 3_600_000).toISOString();

  const membershipOf = (p: Principal) =>
    p.me.memberships.find((m) => m.company_id === p.company.id)?.membership_id ?? null;

  function source(
    companyId: string,
    agentId: string,
    target: Pick<Source, "source_kind" | "page_id" | "space_id" | "page_kind">,
    lastRead: string | null,
    actor: Pick<Source, "actor_kind" | "actor_membership_id">,
  ): Source {
    const at = stamp();
    return {
      company_id: companyId,
      id: nextId(),
      agent_membership_id: agentId,
      ...target,
      last_read_at: lastRead,
      ...actor,
      created_at: at,
      updated_at: at,
    };
  }

  /** The demo's sources: the first DE reads two pages and every SOP; the owner's crew one page. */
  function seed(p: Principal, list: Source[]) {
    const pages = deps.pagesOf(p);
    const byTitle = (t: string) => pages.find((x) => x.title === t && !x.archived_at);
    const system = { actor_kind: "system" as const, actor_membership_id: null };
    const de = deps.desOf(p.company.id)[0];
    const pageSource = (id: string) => ({
      source_kind: "page" as const,
      page_id: id,
      space_id: null,
      page_kind: null,
    });
    const script = byTitle("Phone screen script");
    const bands = byTitle("Salary bands");
    const screening = byTitle("Screening calls");
    if (de) {
      if (script)
        list.push(
          source(p.company.id, de.membership_id, pageSource(script.id), hoursAgo(2), system),
        );
      if (bands)
        list.push(source(p.company.id, de.membership_id, pageSource(bands.id), null, system));
      list.push(
        source(
          p.company.id,
          de.membership_id,
          { source_kind: "kind", page_id: null, space_id: null, page_kind: "sop" },
          hoursAgo(26),
          system,
        ),
      );
    }
    const owner = deps.peopleOf(p.company.id)[0];
    if (owner && screening)
      list.push(
        source(
          p.company.id,
          crewMembershipIdOf(p.company.id, owner.membership_id),
          pageSource(screening.id),
          null,
          system,
        ),
      );
  }

  function storeOf(p: Principal): Source[] {
    let list = stores.get(p.company.id);
    if (!list) {
      list = [];
      stores.set(p.company.id, list);
      seed(p, list);
    }
    return list;
  }

  function agentOf(p: Principal, id: string | undefined): Agent | null {
    if (!id || !UUID.test(id)) return null;
    const de = deps.desOf(p.company.id).find((d) => d.membership_id === id);
    if (de)
      return {
        membership_id: de.membership_id,
        display_name: de.display_name,
        kind: "digital_employee",
        supervisor: de.supervisor.membership_id,
        owner: null,
      };
    const person = deps
      .peopleOf(p.company.id)
      .find((m) => crewMembershipIdOf(p.company.id, m.membership_id) === id);
    if (person)
      return {
        membership_id: id,
        display_name: `${person.name}'s agent`,
        kind: "personal_agent",
        supervisor: null,
        owner: person.membership_id,
      };
    return null;
  }

  /** Who may see the agent: everyone for a DE; a crew's owner and the managers for a crew. */
  const sees = (p: Principal, a: Agent) =>
    a.kind === "digital_employee" || p.manager || membershipOf(p) === a.owner;
  const canEdit = (p: Principal, a: Agent) => {
    const me = membershipOf(p);
    return p.manager || (me !== null && (me === a.supervisor || me === a.owner));
  };

  const live = (pages: PageSummary[]) => pages.filter((x) => !x.archived_at);

  function view(p: Principal, s: Source, pages: PageSummary[]): SourceView {
    const spaces = deps.spacesOf(p.company.id);
    const spaceName = (id: string) => spaces.find((x) => x.id === id)?.name ?? "";
    if (s.source_kind === "page") {
      const pg = pages.find((x) => x.id === s.page_id);
      return {
        ...s,
        page: pg
          ? {
              ...pg,
              created_by_membership_id:
                (pg as { created_by_membership_id?: string | null }).created_by_membership_id ??
                null,
              space_name: spaceName(pg.space_id),
            }
          : null,
        space: null,
        page_count: pg && !pg.archived_at ? 1 : 0,
      };
    }
    if (s.source_kind === "space") {
      const sp = spaces.find((x) => x.id === s.space_id);
      return {
        ...s,
        page: null,
        space: sp ? { id: sp.id, name: sp.name, kind: sp.kind } : null,
        page_count: live(pages).filter((x) => x.space_id === s.space_id).length,
      };
    }
    return {
      ...s,
      page: null,
      space: null,
      page_count: live(pages).filter((x) => x.kind === s.page_kind).length,
    };
  }

  function knowledge(p: Principal, a: Agent): Knowledge {
    const pages = deps.pagesOf(p);
    const mine = storeOf(p)
      .filter((s) => s.agent_membership_id === a.membership_id)
      .sort(
        (x, y) =>
          ORDER[x.source_kind] - ORDER[y.source_kind] || x.created_at.localeCompare(y.created_at),
      );
    const reads = mine.map((s) => s.last_read_at).filter((t): t is string => t !== null);
    return {
      agent: { membership_id: a.membership_id, display_name: a.display_name, kind: a.kind },
      data: mine.map((s) => view(p, s, pages)),
      can_edit: canEdit(p, a),
      last_read_at: reads.sort().at(-1) ?? null,
    };
  }

  /** The write gate: the add-on, then the caller's right to change this agent's list. */
  function writeGate(p: Principal, a: Agent): Response | null {
    const state = deps.agentsState(p);
    if (state === "off") return featureDisabledAgents();
    if (state === "grace")
      return problem(403, "feature_read_only", "Read-only", "The agents add-on is read-only.");
    if (!canEdit(p, a))
      return forbidden("Only the agent's supervisor, its owner and admins change its knowledge.");
    return null;
  }

  const sameTarget = (
    s: Source,
    t: Pick<Source, "source_kind" | "page_id" | "space_id" | "page_kind">,
  ) =>
    s.source_kind === t.source_kind &&
    s.page_id === t.page_id &&
    s.space_id === t.space_id &&
    s.page_kind === t.page_kind;

  const handlers: Partial<Record<RouteId, Handler>> = {
    "agentKnowledge.list": ({ principal, params }) => {
      if (!principal) return notFound();
      const a = agentOf(principal, params.membership_id);
      if (!a || !sees(principal, a)) return notFound();
      return json(knowledge(principal, a));
    },
    "agentKnowledge.add": ({ principal, params, body }) => {
      if (!principal) return notFound();
      const a = agentOf(principal, params.membership_id);
      if (!a || !sees(principal, a)) return notFound();
      const refused = writeGate(principal, a);
      if (refused) return refused;
      const raw = (body as { sources?: unknown } | undefined)?.sources;
      if (!Array.isArray(raw) || raw.length < 1 || raw.length > 50)
        return validation("sources: 1 to 50 sources");
      const pages = deps.pagesOf(principal);
      const spaces = deps.spacesOf(principal.company.id);
      const targets: Pick<Source, "source_kind" | "page_id" | "space_id" | "page_kind">[] = [];
      for (const r of raw as Record<string, unknown>[]) {
        if (r?.kind === "page") {
          const pg = live(pages).find((x) => x.id === r.page_id);
          if (!pg || !deps.readsPages(principal))
            return validation("invalid_reference: page_id is not a page you can view");
          targets.push({ source_kind: "page", page_id: pg.id, space_id: null, page_kind: null });
        } else if (r?.kind === "space") {
          const sp = spaces.find((x) => x.id === r.space_id);
          if (!sp) return validation("invalid_reference: space_id is not a space of the company");
          targets.push({ source_kind: "space", page_id: null, space_id: sp.id, page_kind: null });
        } else if (r?.kind === "kind") {
          if (!WRITABLE_KINDS.includes(r.page_kind as PageKind))
            return validation("page_kind: not a page kind");
          targets.push({
            source_kind: "kind",
            page_id: null,
            space_id: null,
            page_kind: r.page_kind as PageKind,
          });
        } else return validation("sources: kind must be page, space or kind");
      }
      const list = storeOf(principal);
      const actor = { actor_kind: "person" as const, actor_membership_id: membershipOf(principal) };
      for (const t of targets)
        if (!list.some((s) => s.agent_membership_id === a.membership_id && sameTarget(s, t)))
          list.push(source(principal.company.id, a.membership_id, t, null, actor));
      return json(knowledge(principal, a), 201);
    },
    "agentKnowledge.remove": ({ principal, params }) => {
      if (!principal) return notFound();
      const a = agentOf(principal, params.membership_id);
      if (!a || !sees(principal, a)) return notFound();
      const refused = writeGate(principal, a);
      if (refused) return refused;
      const list = storeOf(principal);
      const i = list.findIndex(
        (s) => s.id === params.source_id && s.agent_membership_id === a.membership_id,
      );
      if (i < 0) return notFound();
      list.splice(i, 1);
      return new Response(null, { status: 204 });
    },
    "pages.readers": ({ principal, params }) => {
      if (!principal || !deps.readsPages(principal)) return notFound();
      const pg = deps.pagesOf(principal).find((x) => x.id === params.id);
      if (!pg) return notFound();
      const best = new Map<string, Source>();
      for (const s of storeOf(principal)) {
        const covers =
          (s.source_kind === "page" && s.page_id === pg.id) ||
          (s.source_kind === "space" && s.space_id === pg.space_id) ||
          (s.source_kind === "kind" && s.page_kind === pg.kind);
        if (!covers) continue;
        const had = best.get(s.agent_membership_id);
        if (!had || ORDER[s.source_kind] < ORDER[had.source_kind])
          best.set(s.agent_membership_id, s);
      }
      const data = [...best.values()].flatMap((s) => {
        const a = agentOf(principal, s.agent_membership_id);
        if (!a || !sees(principal, a)) return [];
        return [
          {
            membership_id: a.membership_id,
            display_name: a.display_name,
            kind: a.kind,
            via: s.source_kind,
            last_read_at: s.last_read_at,
          },
        ];
      });
      data.sort((x, y) => x.display_name.localeCompare(y.display_name));
      return json({ data });
    },
  };

  return { handlers };
}
